import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { tc } from '../../i18n/common';
import { 
    LineChart, Line, BarChart, Bar, XAxis, YAxis, 
    CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { message } from 'antd';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F43F5E', '#14B8A6', '#8B5CF6', '#EAB308'];

const CoachInsight = () => {
    const [user, setUser] = useState(null);
    const [locale, setLocale] = useState(localStorage.getItem('locale') || 'en');
    const [appState, setAppState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedOrgs, setSelectedOrgs] = useState({});
    const [timeRange, setTimeRange] = useState('ALL_TIME');
    const [hoveredLine, setHoveredLine] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleLocaleChange = () => {
             setLocale(localStorage.getItem('locale') || 'en');
        };
        window.addEventListener('locale-changed', handleLocaleChange);
        return () => window.removeEventListener('locale-changed', handleLocaleChange);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch User Data to get Tenant ID
                const userResponse = await axios.post('/user');
                const fetchedUser = userResponse.data;
                setUser(fetchedUser);

                const tenantId = fetchedUser?.azure_tenant?.azure_tenant_id || fetchedUser?.azure_tenant_id;

                if (!tenantId) {
                    setError(tc('coachInsight.errorNoTenant', locale) || 'No tenant ID found for this user.');
                    setLoading(false);
                    return;
                }

                const response = await axios.get(`/organigram/${tenantId}/session_data.json`);
                const data = response.data;
                setAppState(data);
                
                // Deselect all orgs by default, showing only Company Average
                if (data && data.organizations) {
                    const initialOrgs = {};
                    data.organizations.forEach(org => {
                        initialOrgs[org.id] = false;
                    });
                    setSelectedOrgs(initialOrgs);
                }
            } catch (err) {
                console.error("Failed to load session data:", err);
                setError(tc('coachInsight.errorLoading', locale) || 'Failed to load session data.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [locale]);


    const filteredSessions = useMemo(() => {
        if (!appState || !appState.sessions) return [];
        const { sessions } = appState;
        const now = new Date();
        
        return sessions.filter(session => {
            if(!session.date) return false;
            const date = new Date(session.date);
            if (timeRange === 'LAST_MONTH') {
                const oneMonthAgo = new Date();
                oneMonthAgo.setMonth(now.getMonth() - 1);
                return date >= oneMonthAgo;
            } else if (timeRange === 'LAST_QUARTER') {
                const oneQuarterAgo = new Date();
                oneQuarterAgo.setMonth(now.getMonth() - 3);
                return date >= oneQuarterAgo;
            } else if (timeRange === 'LAST_12_MONTHS') {
                const oneYearAgo = new Date();
                oneYearAgo.setFullYear(now.getFullYear() - 1);
                return date >= oneYearAgo;
            } else if (timeRange === 'CURRENT_YEAR') {
                const startOfYear = new Date(now.getFullYear(), 0, 1);
                return date >= startOfYear;
            } else if (timeRange === 'LAST_YEAR') {
                const startOfLastYear = new Date(now.getFullYear() - 1, 0, 1);
                const endOfLastYear = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
                return date >= startOfLastYear && date <= endOfLastYear;
            }
            return true;
        });
    }, [appState, timeRange]);

    const orgStats = useMemo(() => {
        if (!appState || !appState.organizations || !appState.powerFactors) return {};
        const { organizations, powerFactors } = appState;
        
        const stats = {};
        organizations.forEach(org => {
            stats[org.id] = { 
                totalSessions: 0, 
                pfSums: {}, 
                pfCounts: {},
                pfAverages: {}
            };
            powerFactors.forEach(pf => {
                stats[org.id].pfSums[pf.id] = 0;
                stats[org.id].pfCounts[pf.id] = 0;
                stats[org.id].pfAverages[pf.id] = 0;
            });
        });

        filteredSessions.forEach(session => {
            session.organizationIds.forEach(orgId => {
                if (stats[orgId]) {
                    stats[orgId].totalSessions += 1;
                    
                    session.powerFactors.forEach(pfScore => {
                        let actualScore = 0;
                        if (Array.isArray(pfScore.score)) {
                            const currentScoreObj = pfScore.score.find(s => s.type === "Current") || pfScore.score[0];
                            if (currentScoreObj) actualScore = currentScoreObj.score;
                        } else if (typeof pfScore.score === 'number') {
                            actualScore = pfScore.score;
                        }

                        if (actualScore > 0 && stats[orgId].pfSums[pfScore.powerFactorId] !== undefined) {
                            stats[orgId].pfSums[pfScore.powerFactorId] += actualScore;
                            stats[orgId].pfCounts[pfScore.powerFactorId] += 1;
                        }
                    });
                }
            });
        });

        // Calculate averages
        organizations.forEach(org => {
            let totalPfScore = 0;
            let numPfsWithData = 0;
            powerFactors.forEach(pf => {
                if (stats[org.id].pfCounts[pf.id] > 0) {
                    const avg = stats[org.id].pfSums[pf.id] / stats[org.id].pfCounts[pf.id];
                    stats[org.id].pfAverages[pf.id] = avg;
                    totalPfScore += avg;
                    numPfsWithData += 1;
                }
            });
            stats[org.id].overallAverage = numPfsWithData > 0 ? (totalPfScore / numPfsWithData) : 0;
        });

        return stats;
    }, [appState, filteredSessions]);

    const handleOrgToggle = (orgId) => {
        setSelectedOrgs(prev => ({...prev, [orgId]: !prev[orgId]}));
    };

    const handleSelectAll = (val) => {
        if (!appState || !appState.organizations) return;
        setSelectedOrgs(appState.organizations.reduce((acc, org) => ({...acc, [org.id]: val}), {}));
    };

    const handleDownloadCSV = () => {
        if (!appState || !appState.powerFactors || !appState.organizations) return;
        const { powerFactors, organizations } = appState;
        
        let csvContent = "";
        const activeOrgs = organizations.filter(org => selectedOrgs[org.id]);
        
        const activePfs = powerFactors.filter(pf => {
            return activeOrgs.some(org => orgStats[org.id] && orgStats[org.id].pfCounts[pf.id] > 0);
        });
        
        const header = [
            tc('coachInsight.department', locale) || "Department", 
            tc('coachInsight.totalSessions', locale) || "Total Sessions", 
            tc('coachInsight.overallAverage', locale) || "Overall Average", 
            ...activePfs.map(pf => pf.name)
        ];
        csvContent += header.map(h => `"${h}"`).join(",") + "\n";
        
        activeOrgs.forEach(org => {
            const stats = orgStats[org.id];
            if (!stats) return;
            const row = [
                `"${org.name}"`, 
                stats.totalSessions, 
                stats.overallAverage > 0 ? stats.overallAverage.toFixed(2) : ""
            ];
            activePfs.forEach(pf => {
                const avg = stats.pfAverages[pf.id];
                row.push(avg > 0 ? avg.toFixed(2) : "");
            });
            csvContent += row.join(",") + "\n";
        });
        
        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const encodedUri = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `statistics_${new Date().toISOString().split('T')[0]}.csv`.replace(/ /g, '_'));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(encodedUri);
    };

    const timeChartData = useMemo(() => {
        if (!appState || !appState.organizations) return [];
        const { organizations } = appState;
        const dataMap = {}; 
        
        filteredSessions.forEach(session => {
            const dateObj = new Date(session.date);
            if (isNaN(dateObj.getTime())) return;
            const monthStr = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
            
            if (!dataMap[monthStr]) {
                dataMap[monthStr] = { date: monthStr, _companyTotal: 0 };
            }
            
            dataMap[monthStr]._companyTotal += 1;
            
            session.organizationIds.forEach(orgId => {
                const org = organizations.find(o => o.id === orgId);
                if (org && selectedOrgs[orgId]) {
                    if (dataMap[monthStr][org.name] === undefined) dataMap[monthStr][org.name] = 0;
                    dataMap[monthStr][org.name] += 1;
                }
            });
        });
        
        const orgCount = organizations.length || 1;
        const avgLabel = tc('coachInsight.companyAvg', locale) || "Company Average";
        
        return Object.values(dataMap).map(item => {
            item[avgLabel] = parseFloat((item._companyTotal / orgCount).toFixed(2));
            delete item._companyTotal;
            return item;
        }).sort((a, b) => a.date.localeCompare(b.date));
    }, [appState, filteredSessions, selectedOrgs, locale]);

    const pfTimelineData = useMemo(() => {
        if (!appState || !appState.powerFactors || !appState.organizations) return {};
        const { powerFactors, organizations } = appState;
        
        const dataMapByPf = {}; 
        
        powerFactors.forEach(pf => {
            dataMapByPf[pf.id] = {};
        });

        filteredSessions.forEach(session => {
            const dateObj = new Date(session.date || session.start);
            if (isNaN(dateObj.getTime())) return;
            const monthStr = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
            
            session.powerFactors.forEach(pfScore => {
                const pfItem = dataMapByPf[pfScore.powerFactorId];
                if (pfItem) {
                    if (!pfItem[monthStr]) pfItem[monthStr] = { date: monthStr, _companySum: 0, _companyCount: 0 };
                    
                    let actualScore = 0;
                    if (Array.isArray(pfScore.score)) {
                        const currentScoreObj = pfScore.score.find(s => s.type === "Current") || pfScore.score[0];
                        if (currentScoreObj) actualScore = currentScoreObj.score;
                    } else if (typeof pfScore.score === 'number') {
                        actualScore = pfScore.score;
                    }

                    if (actualScore > 0) {
                        pfItem[monthStr]._companySum += actualScore;
                        pfItem[monthStr]._companyCount += 1;

                        session.organizationIds.forEach(orgId => {
                            const org = organizations.find(o => o.id === orgId);
                            if (org && selectedOrgs[orgId]) {
                                if (pfItem[monthStr][org.name] === undefined) {
                                    pfItem[monthStr][org.name] = { sum: 0, count: 0 };
                                }
                                pfItem[monthStr][org.name].sum += actualScore;
                                pfItem[monthStr][org.name].count += 1;
                            }
                        });
                    }
                }
            });
        });

        const formattedDataByPf = {};
        const avgLabel = tc('coachInsight.companyAvg', locale) || "Company Average";
        
        powerFactors.forEach(pf => {
            const rawMonthData = Object.values(dataMapByPf[pf.id]).sort((a, b) => a.date.localeCompare(b.date));
            
            const formattedMonthData = rawMonthData.map(monthObj => {
                const finalObj = { date: monthObj.date };
                
                if (monthObj._companyCount > 0) {
                    finalObj[avgLabel] = parseFloat((monthObj._companySum / monthObj._companyCount).toFixed(2));
                }

                organizations.forEach(org => {
                    if (selectedOrgs[org.id] && monthObj[org.name] && monthObj[org.name].count > 0) {
                        finalObj[org.name] = parseFloat((monthObj[org.name].sum / monthObj[org.name].count).toFixed(2));
                    }
                });
                return finalObj;
            });
            
            formattedDataByPf[pf.id] = formattedMonthData;
        });

        return formattedDataByPf;
    }, [appState, filteredSessions, selectedOrgs, locale]);

    if (loading) {
        return (
            <div className="flex h-full items-center justify-center bg-slate-50">
                <div className="text-slate-500 font-medium">{tc('coachInsight.loading', locale) || "Loading statistics..."}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-full items-center justify-center bg-slate-50 p-6">
                <div className="bg-white p-8 rounded-xl shadow-sm border border-red-100 max-w-md text-center">
                    <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{tc('coachInsight.errorTitle', locale) || "Oops, something went wrong"}</h3>
                    <p className="text-slate-500">{error}</p>
                </div>
            </div>
        );
    }

    if (!appState) return null;

    const { organizations, powerFactors, sessions } = appState;
    const activeOrgs = organizations.filter(org => selectedOrgs[org.id]);
    const searchedOrgs = organizations.filter(org => org.name.toLowerCase().includes(searchQuery.toLowerCase()));
    const avgLabel = tc('coachInsight.companyAvg', locale) || "Company Average";

    return (
        <div className="flex flex-col lg:flex-row h-full lg:overflow-hidden text-slate-900" style={{backgroundColor: '#FAFAFA'}}>
            <style>{`
                /* Custom Select styling to remove browser defaults */
                select.custom-select {
                    appearance: none;
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
                    background-position: right 0.75rem center;
                    background-repeat: no-repeat;
                    background-size: 1.25em 1.25em;
                }
                
                /* Subtle custom shadow for minimalist cards */
                .shadow-crafted {
                    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.05);
                }
                
                /* Mobile Bottom Sheet Animation */
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                .animate-slide-up {
                    animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>

            {/* Mobile Overlay & Sidebar bottom sheet */}
            <div className={`
                ${isMobileMenuOpen ? 'fixed inset-0 z-50 flex flex-col justify-end bg-slate-900/40 backdrop-blur-sm' : 'hidden lg:flex'}
                lg:relative lg:w-80 lg:z-auto lg:bg-transparent transition-opacity duration-200
            `} onClick={(e) => { if(e.target === e.currentTarget) setIsMobileMenuOpen(false); }}>
                
                <div className={`
                    w-full bg-white lg:border-r border-slate-100 shadow-2xl lg:shadow-sm flex flex-col p-6 lg:p-8 flex-shrink-0 
                    ${isMobileMenuOpen ? 'h-[85vh] rounded-t-[2rem] pt-8 animate-slide-up relative' : 'lg:h-full lg:overflow-hidden lg:rounded-none lg:w-full'}
                `}>
                    {/* Close Button on Mobile */}
                    {isMobileMenuOpen && (
                        <button autoFocus onClick={() => setIsMobileMenuOpen(false)} className="absolute top-5 right-5 p-2 text-slate-400 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 rounded-full transition-colors lg:hidden" aria-label="Close filters">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                    )}
                
                <div className="mb-8 flex-shrink-0">
                    <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-3">{tc('coachInsight.timeRangeLabel', locale) || "Time Range"}</h3>
                    <select 
                        value={timeRange} 
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="custom-select w-full border border-slate-200 rounded-lg p-2.5 text-sm bg-white hover:border-slate-300 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none transition-colors cursor-pointer"
                    >
                        <option value="ALL_TIME">{tc('coachInsight.time.all', locale) || "All Time"}</option>
                        <option value="CURRENT_YEAR">{tc('coachInsight.time.currentYear', locale) || "Current Year"}</option>
                        <option value="LAST_YEAR">{tc('coachInsight.time.lastYear', locale) || "The Last Year"}</option>
                        <option value="LAST_12_MONTHS">{tc('coachInsight.time.last12Months', locale) || "Last 12 Months"}</option>
                        <option value="LAST_QUARTER">{tc('coachInsight.time.lastQuarter', locale) || "Last Quarter"}</option>
                        <option value="LAST_MONTH">{tc('coachInsight.time.lastMonth', locale) || "Last Month"}</option>
                    </select>
                </div>

                <div className="flex-shrink-0 pr-2">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">
                            {tc('coachInsight.departmentsLabel', locale) || "Departments"} ({activeOrgs.length}/{organizations.length})
                        </h3>
                    </div>
                    
                    <div className="mb-4 relative">
                        <input 
                            type="text" 
                            placeholder={tc('coachInsight.searchDepartments', locale) || "Search departments..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900 transition-colors"
                        />
                        <svg className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                    </div>

                    <div className="flex gap-2 mb-4">
                        <button onClick={() => handleSelectAll(true)} className="flex-1 text-xs font-medium bg-slate-50 border border-slate-100 hover:bg-slate-100 text-slate-600 py-1.5 rounded transition duration-150">{tc('coachInsight.selectAll', locale) || "All"}</button>
                        <button onClick={() => handleSelectAll(false)} className="flex-1 text-xs font-medium bg-slate-50 border border-slate-100 hover:bg-slate-100 text-slate-600 py-1.5 rounded transition duration-150">{tc('coachInsight.selectNone', locale) || "None"}</button>
                    </div>
                </div>

                <div className="flex-1 space-y-0.5 overflow-y-auto pr-2 pb-2">
                    {searchedOrgs.map((org, index) => (
                            <label key={org.id} className="flex items-center space-x-3 text-[13px] text-slate-700 cursor-pointer group hover:bg-slate-50 py-1.5 px-2 rounded-md transition duration-150 -mx-2">
                                <div className="flex items-center h-5">
                                    <input 
                                        type="checkbox" 
                                        checked={!!selectedOrgs[org.id]} 
                                        onChange={() => handleOrgToggle(org.id)}
                                        className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-transparent focus:ring-offset-0 transition-colors cursor-pointer"
                                        style={{accentColor: '#0f172a'}}
                                    />
                                </div>
                                <div className="flex items-center space-x-2.5 w-full">
                                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{backgroundColor: selectedOrgs[org.id] ? COLORS[index % COLORS.length] : '#e2e8f0'}}></div>
                                    <span className="truncate leading-5 font-medium select-none flex-1" title={org.name}>{org.name}</span>
                                    {orgStats[org.id] && orgStats[org.id].overallAverage > 0 && (
                                        <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                                            {orgStats[org.id].overallAverage.toFixed(1)}
                                        </span>
                                    )}
                                </div>
                            </label>
                        ))}
                </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-4 sm:p-8 lg:p-10 overflow-y-auto w-full">
                <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8">
                        
                        {/* Header & Inline KPIs */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end border-b border-slate-200 pb-6 mb-6 sm:mb-8 mt-2 gap-4 sm:gap-0">
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                                    {tc('coachInsight.defaultTitle', locale) || "1:1 Coach Insight"}
                                </h1>
                                <p className="text-sm text-slate-500 mt-2">{tc('coachInsight.subtitle', locale) || "Viewing statistics for the selected time range and departments."}</p>
                                
                                <div className="flex flex-wrap gap-2 sm:gap-3 mt-4">
                                    <button onClick={() => setIsMobileMenuOpen(true)} className="lg:hidden inline-flex items-center px-4 py-2 bg-slate-900 text-white text-[13px] font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path></svg>
                                        {tc('coachInsight.filterDeptsBtn', locale) || "Filter Departments"}
                                    </button>
                                    <button onClick={handleDownloadCSV} className="inline-flex items-center px-4 py-2 bg-white border border-slate-200 text-slate-700 text-[13px] font-medium rounded-lg hover:bg-slate-50 transition-colors shadow-sm focus:ring-2 focus:ring-slate-900 focus:outline-none">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                                        {tc('coachInsight.downloadBtn', locale) || "Download Data Report (CSV)"}
                                    </button>
                                </div>
                            </div>
                            <div className="flex space-x-8 sm:space-x-12 text-left sm:text-right w-full sm:w-auto">
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1">{tc('coachInsight.totalSessions', locale) || "Total Sessions"}</p>
                                    <p className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{filteredSessions.length}</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1">{tc('coachInsight.activeDepts', locale) || "Active Depts"}</p>
                                    <p className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{activeOrgs.length}</p>
                                </div>
                            </div>
                        </div>

                        {/* Leaderboards */}
                        <div className="mb-10">
                            <h3 className="text-2xl font-semibold mb-6 tracking-tight text-slate-900 border-b border-slate-200 pb-4">{tc('coachInsight.deptLeaderboards', locale) || "Department Leaderboards"}</h3>
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                                {powerFactors.map(pf => {
                                    const rankedOrgs = organizations
                                        .filter(org => orgStats[org.id] && orgStats[org.id].pfCounts[pf.id] > 0)
                                        .map(org => ({ 
                                            name: org.name, 
                                            score: orgStats[org.id].pfAverages[pf.id] 
                                        }))
                                        .sort((a, b) => b.score - a.score);
                                    
                                    if (rankedOrgs.length === 0) return null;
                                    
                                    const top3 = rankedOrgs.slice(0, 3);
                                    const bottom3 = rankedOrgs.slice(Math.max(rankedOrgs.length - 3, 3)).reverse();
                                    
                                    return (
                                        <div key={pf.id} className="bg-white p-6 rounded-xl shadow-crafted border border-slate-100 overflow-hidden">
                                            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-6 pb-4 border-b border-slate-100">{pf.name}</h4>
                                            <div className="flex flex-col sm:flex-row gap-8 sm:gap-6">
                                                <div className="flex-1 border-b sm:border-b-0 sm:border-r border-slate-100 pb-6 sm:pb-0 sm:pr-6 min-w-0">
                                                    <h5 className="text-[11px] font-bold text-emerald-500 uppercase tracking-widest mb-4">{tc('coachInsight.topPerformers', locale) || "Top Performers"}</h5>
                                                    <ul className="space-y-3">
                                                        {top3.map((item, idx) => (
                                                            <li key={idx} className="flex justify-between items-center text-sm">
                                                                <span className="text-slate-700 truncate pr-3 font-medium flex-1 min-w-0" title={item.name}>{item.name}</span>
                                                                <span className="font-semibold text-slate-900 bg-slate-50 px-2 py-0.5 rounded ml-2 flex-shrink-0">{item.score.toFixed(1)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div className="flex-1 sm:pl-2 min-w-0">
                                                    <h5 className="text-[11px] font-bold text-rose-500 uppercase tracking-widest mb-4">{tc('coachInsight.needsAttention', locale) || "Needs Attention"}</h5>
                                                    <ul className="space-y-3">
                                                        {bottom3.map((item, idx) => (
                                                            <li key={idx} className="flex justify-between items-center text-sm">
                                                                <span className="text-slate-700 truncate pr-3 font-medium flex-1 min-w-0" title={item.name}>{item.name}</span>
                                                                <span className="font-semibold text-slate-900 bg-slate-50 px-2 py-0.5 rounded ml-2 flex-shrink-0">{item.score.toFixed(1)}</span>
                                                            </li>
                                                        ))}
                                                        {bottom3.length === 0 && <li className="text-sm text-slate-400 italic">Not enough data</li>}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Time Chart */}
                        <div className="bg-white p-4 sm:p-8 rounded-xl shadow-crafted h-96 sm:h-[450px]">
                            <h3 className="text-base font-semibold mb-6 sm:mb-8 tracking-tight">{tc('coachInsight.sessionsOverTime', locale) || "Sessions Over Time"}</h3>
                            <ResponsiveContainer width="100%" height="90%">
                                <LineChart data={timeChartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9"/>
                                    <XAxis dataKey="date" tick={{fill: '#94A3B8', fontSize: 11}} axisLine={false} tickLine={false} tickMargin={16}/>
                                    <YAxis tick={{fill: '#94A3B8', fontSize: 11}} axisLine={false} tickLine={false} tickMargin={16}/>
                                    <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)', fontSize: '12px'}}/>
                                    <Legend wrapperStyle={{paddingTop: '32px', fontSize: '12px', opacity: 0.8}} onMouseEnter={(e) => setHoveredLine(e.dataKey)} onMouseLeave={() => setHoveredLine(null)}/>
                                    <Line 
                                        connectNulls={true} 
                                        type="monotone" 
                                        dataKey={avgLabel} 
                                        stroke="#0f172a" 
                                        strokeWidth={3} 
                                        strokeDasharray="5 5"
                                        dot={false}
                                        activeDot={{r: 5, strokeWidth: 0}} 
                                        isAnimationActive={false}
                                    />
                                    {activeOrgs.map((org, index) => {
                                        const isHovered = hoveredLine === org.name;
                                        const opacity = hoveredLine && !isHovered ? 0.15 : 1;
                                        return (
                                            <Line 
                                                key={org.id} 
                                                connectNulls={true} 
                                                type="monotone" 
                                                dataKey={org.name} 
                                                stroke={COLORS[index % COLORS.length]} 
                                                strokeWidth={isHovered ? 3 : 2} 
                                                strokeOpacity={opacity}
                                                dot={false} 
                                                activeDot={{r: 5, strokeWidth: 0}} 
                                                animationDuration={800}
                                                onMouseEnter={() => setHoveredLine(org.name)}
                                                onMouseLeave={() => setHoveredLine(null)}
                                            />
                                        );
                                    })}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* PF Charts */}
                        {powerFactors.map(pf => {
                            const data = pfTimelineData[pf.id];
                            if (!data || data.length === 0) return null;
                            
                            return (
                                <div key={pf.id} className="bg-white p-4 sm:p-8 rounded-xl shadow-crafted h-96 sm:h-[450px]">
                                    <h3 className="text-base font-semibold mb-6 sm:mb-8 tracking-tight">{pf.name} {tc('coachInsight.trendScore', locale) || "Trend (Score)"}</h3>
                                    <ResponsiveContainer width="100%" height="90%">
                                        <LineChart data={data}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9"/>
                                            <XAxis dataKey="date" tick={{fill: '#94A3B8', fontSize: 11}} axisLine={false} tickLine={false} tickMargin={16}/>
                                            <YAxis domain={[0, 100]} tick={{fill: '#94A3B8', fontSize: 11}} axisLine={false} tickLine={false} tickMargin={16}/>
                                            <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)', fontSize: '12px'}}/>
                                            <Legend wrapperStyle={{paddingTop: '32px', fontSize: '12px', opacity: 0.8}} onMouseEnter={(e) => setHoveredLine(e.dataKey)} onMouseLeave={() => setHoveredLine(null)}/>
                                            <Line 
                                                connectNulls={true} 
                                                type="monotone" 
                                                dataKey={avgLabel} 
                                                stroke="#0f172a" 
                                                strokeWidth={3} 
                                                strokeDasharray="5 5"
                                                dot={false}
                                                activeDot={{r: 5, strokeWidth: 0}} 
                                                isAnimationActive={false}
                                            />
                                            {activeOrgs.map((org, index) => {
                                                const isHovered = hoveredLine === org.name;
                                                const opacity = hoveredLine && !isHovered ? 0.15 : 1;
                                                return (
                                                    <Line 
                                                        key={org.id} 
                                                        connectNulls={true} 
                                                        type="monotone" 
                                                        dataKey={org.name} 
                                                        stroke={COLORS[index % COLORS.length]} 
                                                        strokeWidth={isHovered ? 3 : 2} 
                                                        strokeOpacity={opacity}
                                                        dot={false} 
                                                        activeDot={{r: 5, strokeWidth: 0}} 
                                                        animationDuration={800}
                                                        onMouseEnter={() => setHoveredLine(org.name)}
                                                        onMouseLeave={() => setHoveredLine(null)}
                                                    />
                                                );
                                            })}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            );
                        })}

                </div>
            </div>
        </div>
    );
};

export default CoachInsight;
